import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Clock
} from 'lucide-react';
import './MonthlyComplianceCalendar.css';

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

const IST_TIMEZONE = 'Asia/Kolkata';

const pad2 = (value) => String(value).padStart(2, '0');

/**
 * Today as the BACKEND sees it.
 *
 * Every operation_date in DailyOperations is an IST calendar date, so the
 * browser's own idea of "today" is the wrong ruler — a user in another
 * timezone would otherwise see the current day drift by one cell.
 */
const getTodayISO = () => new Date().toLocaleDateString('en-CA', { timeZone: IST_TIMEZONE });

/** 'YYYY-MM-DD' for a calendar day, built without ever crossing a timezone. */
const toISODate = (year, month, day) => `${year}-${pad2(month + 1)}-${pad2(day)}`;

/**
 * Whatever the API hands back, reduced to 'YYYY-MM-DD'.
 * DATE columns come through as plain strings, but a driver or a serialiser
 * that turns one into an ISO timestamp must not shift the day.
 */
const normaliseISODate = (value) => {
  if (!value) return null;
  const text = String(value).split('T')[0].trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
};

/** Indian-format rupees, no paise — a calendar cell is not a ledger. */
const formatMoney = (value) => `₹${Math.round(Number(value) || 0).toLocaleString('en-IN')}`;

const CHECKPOINT_LABELS = {
  stockIn: 'Stock-In',
  wastage: 'Wastage',
  tsr: 'TSR'
};

/**
 * The rule the whole calendar exists to express:
 *
 *   Wastage AND TSR both recorded -> complete (green)
 *   something recorded, but not both -> partial (orange)
 *   nothing recorded at all          -> missed  (outline)
 *
 * Green is a statement about the day being closed out, and a day is closed out
 * by two acts: the wastage for it was recorded — with a reason against it, even
 * when that reason is "No Wastage Recorded" — and the sales report was
 * submitted. Either one missing leaves the day orange.
 *
 * Stock-In is deliberately NOT one of the two. Deliveries do not arrive every
 * day, so a day with no stock-in is usually a day with nothing to receive, and
 * requiring it would hold a fully reconciled day at orange forever. It still
 * counts towards "something was recorded here", so a stock-in-only day reads as
 * partial rather than untouched, and the tooltip still names it.
 */
const getDayStatus = (record, { todayISO, trackingStartDate }) => {
  if (record.date > todayISO) return "future";
  if (trackingStartDate && record.date < trackingStartDate) return "untracked";

  if (record.wastage && record.tsr) return "complete";
  if (record.stockIn || record.wastage || record.tsr) return "partial";
  return "missed";
};

const describeRecord = (record, status) => {
  const prettyDate = `${record.day} ${MONTH_NAMES[record.month]} ${record.year}`;
  if (status === "future") return `${prettyDate} — upcoming`;
  if (status === "untracked") return `${prettyDate} — before tracking started`;

  const done = [];
  const missing = [];
  Object.keys(CHECKPOINT_LABELS).forEach(key => {
    (record[key] ? done : missing).push(CHECKPOINT_LABELS[key]);
  });

  const money = record.totalSales > 0
    ? ` | sales ${formatMoney(record.totalSales)}, profit ${record.grossProfit === null ? 'unknown' : formatMoney(record.grossProfit)}`
    : '';

  // A green day says so in the words of the rule that made it green, and names
  // Stock-In only as the aside it is — never as something the day is missing.
  if (status === "complete") {
    return missing.length === 0
      ? `${prettyDate} — all 3 done${money}`
      : `${prettyDate} — Wastage and TSR done; no Stock-In${money}`;
  }

  if (done.length === 0) return `${prettyDate} — nothing recorded${money}`;
  return `${prettyDate} — done: ${done.join(', ')}; missing: ${missing.join(', ')}${money}`;
};

export function MonthlyComplianceCalendar({
  businessId,
  onSelectDay,
  currentOperationStatus
}) {
  const todayISO = getTodayISO();
  const [todayYear, todayMonth] = todayISO.split('-').map(Number);

  // Open on the month the owner is actually living in.
  const [currentDate, setCurrentDate] = useState(() => new Date(todayYear, todayMonth - 1, 1));
  const [selectedDate, setSelectedDate] = useState(todayISO);
  const [operationsByDate, setOperationsByDate] = useState({});
  const [monthTotals, setMonthTotals] = useState(null);
  const [trackingStartDate, setTrackingStartDate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthLabel = `${MONTH_NAMES[month]} ${year}`;

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstWeekday = new Date(year, month, 1).getDay();

  const monthStart = toISODate(year, month, 1);
  const monthEnd = toISODate(year, month, daysInMonth);

  // Today's card already holds the live truth for today; the calendar should
  // not lag behind it by up to a refresh cycle.
  //
  // The onboarding stub reports every checkpoint as done purely to disable the
  // locks. It carries no operationId — and painting today green on the strength
  // of it would be a lie about a day nothing was recorded on.
  const liveTodayApplies = Boolean(
    currentOperationStatus?.operationId &&
    normaliseISODate(currentOperationStatus.operationDate) === todayISO
  );
  const liveStockIn = !!currentOperationStatus?.stockinCompleted;
  // `wastageSubstantive`, not `wastageRecorded`. The two differ on exactly one
  // day: one whose only wastage artefact is a "No Wastage Recorded" declaration.
  // That day has ANSWERED its wastage question — so `wastageRecorded` is true
  // and the TSR is unlocked — but it has recorded nothing, so the calendar must
  // not colour it as documented. Reading the lock's field here is what painted
  // a whole month green over an empty Stock-Out ledger.
  const liveWastage = !!currentOperationStatus?.wastageSubstantive;
  const liveTsr = !!currentOperationStatus?.tsrSubmitted;

  // Keyed on the four primitives, not the polled object, so a 30-second refresh
  // that changes nothing does not churn the grid.
  const liveToday = useMemo(() => {
    if (!liveTodayApplies) return null;
    return { stockIn: liveStockIn, wastage: liveWastage, tsr: liveTsr };
  }, [liveTodayApplies, liveStockIn, liveWastage, liveTsr]);

  const fetchMonth = useCallback(async (signal) => {
    const token = localStorage.getItem('token');
    const activeBusinessId = businessId || localStorage.getItem('businessId');

    if (!token || !activeBusinessId) {
      setIsLoading(false);
      setLoadError('Not signed in');
      return;
    }

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/operations/history?start_date=${monthStart}&end_date=${monthEnd}`,
        {
          signal,
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-business-id': activeBusinessId
          }
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success || !Array.isArray(result.data)) {
        throw new Error(result.error || `Request failed (${response.status})`);
      }

      const map = {};
      result.data.forEach(row => {
        const date = normaliseISODate(row.operation_date);
        if (!date) return;
        map[date] = {
          stockIn: !!row.stockin_completed,
          wastage: !!row.wastage_recorded,
          tsr: !!row.tsr_submitted,
          totalSales: Number(row.total_sales || 0),
          // null is a real value here — "could not be costed" — and must survive the
          // trip intact rather than becoming a 0 that reads as "broke even".
          grossProfit: row.gross_profit === null || row.gross_profit === undefined
            ? null
            : Number(row.gross_profit),
          grossProfitPartial: !!row.gross_profit_partial
        };
      });

      setOperationsByDate(map);
      setMonthTotals(result.totals || null);
      setTrackingStartDate(normaliseISODate(result.trackingPeriod?.trackingStartDate));
      setLoadError(null);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.warn('Unable to load compliance history:', err);
      // No invented data: an unreadable month stays blank rather than
      // asserting a compliance record that was never fetched.
      setOperationsByDate({});
      setMonthTotals(null);
      setLoadError('Unable to load compliance history');
    } finally {
      if (!signal || !signal.aborted) setIsLoading(false);
    }
  }, [businessId, monthStart, monthEnd]);

  useEffect(() => {
    const controller = new AbortController();
    fetchMonth(controller.signal);
    return () => controller.abort();
  }, [fetchMonth]);

  // Re-read the month whenever today's checkpoints move, so finishing a
  // checkpoint is reflected on the grid without a manual reload.
  useEffect(() => {
    if (!liveToday) return;
    if (todayISO < monthStart || todayISO > monthEnd) return;
    setOperationsByDate(prev => ({ ...prev, [todayISO]: liveToday }));
  }, [liveToday, todayISO, monthStart, monthEnd]);

  const records = useMemo(() => {
    return Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      const date = toISODate(year, month, day);
      const record = operationsByDate[date];

      return {
        day,
        month,
        year,
        date,
        stockIn: !!record?.stockIn,
        wastage: !!record?.wastage,
        tsr: !!record?.tsr,
        totalSales: Number(record?.totalSales || 0),
        // Three states, and conflating the last two is how a quiet day starts claiming
        // its profit is unknown:
        //   a number  costed
        //   null      the day HAS a row and its cost could not be established
        //   0         no row at all — nothing was sold, so nothing is owed an answer
        grossProfit: record ? (record.grossProfit ?? null) : 0,
        grossProfitPartial: !!record?.grossProfitPartial
      };
    });
  }, [daysInMonth, year, month, operationsByDate]);

  const prevMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleCellClick = (record) => {
    setSelectedDate(record.date);
    if (onSelectDay) onSelectDay(record.day, record.date, record);
  };

  // Nothing to show past the current month — the data cannot exist yet.
  const isViewingCurrentMonth = year === todayYear && month === todayMonth - 1;

  // The day the money panel describes: whichever cell is selected, falling back to
  // nothing rather than to an arbitrary day, so the panel never states a figure the
  // reader did not ask for.
  const selectedRecord = records.find((r) => r.date === selectedDate) || null;
  const selectedStatus = selectedRecord
    ? getDayStatus(selectedRecord, { todayISO, trackingStartDate })
    : null;
  // Only for a day that actually took money. A day with no sales showing "₹0 sales,
  // ₹0 profit" is noise that reads like a reported result, and most days on a freshly
  // opened month are exactly that.
  const showSelectedMoney = !!selectedRecord
    && selectedStatus !== 'future'
    && selectedStatus !== 'untracked'
    && (selectedRecord.totalSales > 0 || selectedRecord.grossProfit === null);

  return (
    <div className={`calendar-inline-card ${isLoading ? 'is-loading' : ''}`}>
      {/* Month Navigation */}
      <div className="calendar-nav-bar">
        <button
          type="button"
          className="calendar-nav-btn"
          onClick={prevMonth}
          aria-label="Previous month"
        >
          <ChevronLeft className="size-4" />
        </button>

        <h3 className="calendar-month-heading">{monthLabel}</h3>

        <button
          type="button"
          className="calendar-nav-btn"
          onClick={nextMonth}
          disabled={isViewingCurrentMonth}
          aria-label="Next month"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="calendar-weekdays-grid">
        {WEEKDAYS.map((day, idx) => (
          <div key={`${day}-${idx}`} className="calendar-weekday-cell">
            {day}
          </div>
        ))}
      </div>

      {/* Days Grid */}
      <div className="calendar-days-grid">
        {/* Padding slots */}
        {Array.from({ length: firstWeekday }).map((_, i) => (
          <div key={`pad-${i}`} className="calendar-pad-slot" />
        ))}

        {/* Day Cells */}
        {records.map((r) => {
          const status = getDayStatus(r, { todayISO, trackingStartDate });
          const isSelected = selectedDate === r.date;
          const label = describeRecord(r, status);

          return (
            <button
              key={r.date}
              type="button"
              onClick={() => handleCellClick(r)}
              className={`calendar-day-button ${isSelected ? 'selected' : ''}`}
              title={label}
              aria-label={label}
            >
              {/* Disc */}
              <span className={`calendar-disc-element ${status} ${isSelected ? 'selected-ring' : ''}`} />

              {/* Day Number */}
              <span className={`calendar-day-text ${status}`}>
                {r.day}
              </span>

              {/* Complete Tick Mark */}
              {status === "complete" && (
                <Check
                  className="calendar-tick-icon"
                  strokeWidth={3.5}
                />
              )}
            </button>
          );
        })}
      </div>

      {loadError && (
        <p className="calendar-load-error">{loadError}</p>
      )}

      {/* Sales and gross profit — the month, and the day in focus.
          Both halves of every bill are costed: menu lines from the deduction engine,
          "Not in Menu" lines at the measured rate (see utils/nonMenuCosting.js). A day
          whose cost could not be established shows a dash, never a zero. */}
      {(showSelectedMoney || monthTotals) && (
        <div className="calendar-money-panel">
          {showSelectedMoney && (
            <div className="calendar-money-row">
              <span className="calendar-money-label">
                {selectedRecord.day} {MONTH_NAMES[selectedRecord.month]}
              </span>
              <span className="calendar-money-figures">
                <span className="calendar-money-sales">{formatMoney(selectedRecord.totalSales)}</span>
                <span className="calendar-money-sep">sales</span>
                <span className={`calendar-money-profit ${selectedRecord.grossProfit !== null && selectedRecord.grossProfit < 0 ? 'is-loss' : ''}`}>
                  {selectedRecord.grossProfit === null ? '—' : formatMoney(selectedRecord.grossProfit)}
                </span>
                <span className="calendar-money-sep">
                  {selectedRecord.grossProfit !== null && selectedRecord.grossProfit < 0 ? 'loss' : 'profit'}
                </span>
              </span>
            </div>
          )}

          {monthTotals && (
            <div className="calendar-money-row is-total">
              <span className="calendar-money-label">{MONTH_NAMES[month]} total</span>
              <span className="calendar-money-figures">
                <span className="calendar-money-sales">{formatMoney(monthTotals.totalSales)}</span>
                <span className="calendar-money-sep">sales</span>
                <span className={`calendar-money-profit ${Number(monthTotals.grossProfit) < 0 ? 'is-loss' : ''}`}>
                  {formatMoney(monthTotals.grossProfit)}{monthTotals.grossProfitPartial ? '*' : ''}
                </span>
                <span className="calendar-money-sep">
                  {Number(monthTotals.grossProfit) < 0 ? 'loss' : 'profit'}
                </span>
              </span>
            </div>
          )}

          {monthTotals && monthTotals.grossProfitPartial && (
            <p className="calendar-money-note">
              * excludes days whose cost could not be established
            </p>
          )}
        </div>
      )}


      {/* Legend Footer */}
      <div className="calendar-legend-bar">
        <div className="legend-entry">
          <span className="legend-disc complete">
            <Check style={{ width: '10px', height: '10px', color: '#ffffff' }} strokeWidth={3} />
          </span>
          <span>Wastage + TSR done</span>
        </div>
        <div className="legend-entry">
          <span className="legend-disc partial">
            <Clock style={{ width: '9px', height: '9px', color: '#b45309' }} strokeWidth={2.5} />
          </span>
          <span>Partially done</span>
        </div>
        <div className="legend-entry">
          <span className="legend-disc missed" />
          <span>Nothing recorded</span>
        </div>
      </div>
    </div>
  );
}

export default MonthlyComplianceCalendar;
